"use client";

import React from "react";
import { Axe, LayoutTemplate, MousePointerClick, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const benefits = ["100% Exclusive Leads generated under your own brand identity.", "Higher conversion rates due to pre-established brand trust.", "Custom-built, high-converting landing pages tailored to your offers.", "Real-time delivery directly to your CRM or sales team.", "Scalable campaigns focused on home improvement sectors."];
export default function BrandedCampaigns() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="branded-campaigns" className="dark bg-background text-foreground py-24 lg:py-32 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute top-[60%] -right-[10%] w-[40%] h-[60%] rounded-full bg-chart-4/5 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {}
          <div ref={leftRef} className={`relative transition-all duration-1000 ease-out ${isLeftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="relative mx-auto max-w-[500px] lg:max-w-none">
              {}
              <div className="absolute -inset-1 bg-gradient-to-tr from-primary/20 via-transparent to-chart-4/20 rounded-2xl blur-2xl opacity-70" />

              <Card className="relative border-border/40 bg-card/60 backdrop-blur-xl shadow-2xl overflow-hidden rounded-2xl">
                {}
                <div className="bg-muted/20 border-b border-border/40 px-4 py-3 flex items-center gap-3">
                  <div className="flex gap-1.5">
                    <div className="size-3 rounded-full bg-destructive/70" />
                    <div className="size-3 rounded-full bg-chart-4/70" />
                    <div className="size-3 rounded-full bg-primary/70" />
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="h-6 w-2/3 max-w-[200px] bg-background/40 rounded-md border border-border/30 flex items-center px-3">
                      <div className="h-1.5 w-1/2 bg-muted-foreground/30 rounded-sm" />
                    </div>
                  </div>
                </div>

                {}
                <div className="p-6 sm:p-8 space-y-8">
                  {}
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                        <LayoutTemplate className="size-5 text-primary" />
                      </div>
                      <div className="h-5 w-28 bg-foreground/10 rounded" />
                    </div>
                    <div className="hidden sm:block h-9 w-28 bg-primary/10 rounded-full border border-primary/20" />
                  </div>

                  {}
                  <div className="space-y-5 text-center pt-4">
                    <div className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                      Exclusive Offer
                    </div>
                    <h3 className="text-3xl sm:text-4xl font-bold leading-tight text-foreground tracking-tight">
                      Transform Your Home Today
                    </h3>
                    <p className="text-base text-muted-foreground mx-auto max-w-[85%]">
                      Get a free, no-obligation quote for your next premium remodeling project.
                    </p>
                  </div>

                  {}
                  <div className="space-y-4 p-6 rounded-xl border border-border/40 bg-background/40 shadow-inner">
                    <div className="space-y-3">
                      <div className="h-11 w-full bg-input/20 rounded-md border border-border/30 flex items-center px-4">
                         <div className="h-2 w-24 bg-muted-foreground/20 rounded-sm" />
                      </div>
                      <div className="h-11 w-full bg-input/20 rounded-md border border-border/30 flex items-center px-4">
                         <div className="h-2 w-32 bg-muted-foreground/20 rounded-sm" />
                      </div>
                    </div>
                    <div className="h-12 w-full bg-primary hover:bg-primary/90 transition-colors rounded-md flex items-center justify-center gap-2 shadow-lg shadow-primary/20 cursor-pointer mt-2">
                      <span className="text-primary-foreground font-semibold">Get My Free Quote</span>
                      <MousePointerClick className="size-4 text-primary-foreground animate-pulse" />
                    </div>
                  </div>
                  
                  {}
                  <div className="flex justify-center gap-4 pt-2 opacity-40">
                     <div className="h-6 w-16 bg-foreground/20 rounded" />
                     <div className="h-6 w-16 bg-foreground/20 rounded" />
                     <div className="h-6 w-16 bg-foreground/20 rounded" />
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`space-y-8 transition-all duration-1000 delay-200 ease-out ${isRightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center rounded-full border border-border bg-muted/50 px-3 py-1 text-sm font-medium text-muted-foreground">
                Identity-Driven Growth
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                Own Your Market with <span className="text-primary">Branded Campaigns</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                Stop competing for shared attention. Our branded campaigns generate exclusive, high-intent leads that already know and trust your name before you even pick up the phone.
              </p>
            </div>

            <ul className="space-y-5">
              {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-4 group">
                  <div className="mt-1 bg-primary/10 p-1.5 rounded-md border border-primary/20 group-hover:bg-primary/20 transition-colors shrink-0">
                    <Axe className="size-5 text-primary transform -rotate-45" />
                  </div>
                  <span className="text-foreground/90 text-lg leading-snug pt-1">
                    {benefit}
                  </span>
                </li>)}
            </ul>

            <div className="pt-6">
              <Button asChild size="lg" className="h-12 px-8 text-base group">
                <Link to="/ReserveYourDemoPage">
                  Explore Branded Campaigns
                  <ArrowRight className="ml-2 size-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}